import { CustomerFormFields } from "@/components/customers/CustomerFormFields";
import Layout from "@/components/Layout";
import { formatCurrency } from "@/lib/currencies/currenciesStore";
import PermissionDenied from "@/components/PermissionDenied";
import { ConfirmAlertDialog } from "@/components/ConfirmAlertDialog";
import { CatalogEntityCard, formatCatalogAuditDate } from "@/components/catalog/CatalogEntityCard";
import { GridPaginationFooter, GridPerPageSelect } from "@/components/catalog/GridPaginationControls";
import { PartyUpsertDialog } from "@/components/parties/PartyUpsertDialog";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetClose, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { toast } from "@/components/ui/sonner";
import { useGridPagination } from "@/hooks/useGridPagination";
import { partyEntityKindLabel } from "@/lib/parties/entityKind";
import { partyTypeLabel } from "@/lib/parties/partyTypes";
import {
  createCustomer,
  customerErrorMessage,
  deleteCustomer,
  emptyCustomerDraft,
  formatCustomerDisplayName,
  isCustomerDraftComplete,
  listCustomers,
  normalizeCustomerDraft,
  recordToCustomerDraft,
  syncCustomersFromApi,
  updateCustomer,
  validateCustomerDraft,
  type CustomerDraft,
  type CustomerRecord,
} from "@/lib/customers/customersStore";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { cn } from "@/lib/utils";
import { isAxiosError } from "axios";
import { endOfDay, format, isValid, parseISO, startOfDay, startOfMonth, subDays } from "date-fns";
import { CalendarIcon, Edit, Eye, Filter, LayoutGrid, List, Loader2, MapPin, Plus, Search, ShoppingBag, Trash2, User, Users } from "lucide-react";
import { motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useState } from "react";

const defaultDraft: CustomerDraft = emptyCustomerDraft();

const CUSTOMERS_PAGE_SIZE_OPTIONS = [20, 24, 28, 32, 40, 48, 60] as const;

type CustomerSortBy = "spent_desc" | "spent_asc" | "latest" | "oldest" | "name_asc" | "name_desc";

function formatCustomerSpent(amount: number, currency?: string): string {
  if (!Number.isFinite(amount)) return "—";
  return formatCurrency(amount, currency);
}

function formatCreatedAt(iso: string): string {
  const d = parseISO(iso);
  return isValid(d) ? format(d, "PP") : "—";
}

const Customers = () => {
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canViewAny = can(user, "customer_can_view_any");
  const canView = can(user, "customer_can_view");
  const canCreate = can(user, "customer_can_create");
  const canUpdate = can(user, "customer_can_update");
  const canDelete = can(user, "customer_can_delete");
  const [isForbidden, setIsForbidden] = useState(false);

  const [version, setVersion] = useState(0);
  const refresh = useCallback(() => setVersion((v) => v + 1), []);

  const [query, setQuery] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [viewCustomer, setViewCustomer] = useState<CustomerRecord | null>(null);
  const [editing, setEditing] = useState<CustomerRecord | null>(null);
  const [draft, setDraft] = useState<CustomerDraft>(defaultDraft);
  const [deleteTarget, setDeleteTarget] = useState<CustomerRecord | null>(null);
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [sortBy, setSortBy] = useState<CustomerSortBy>("spent_desc");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterCityId, setFilterCityId] = useState<string>("__all__");
  const [filterCreatedDate, setFilterCreatedDate] = useState<Date | undefined>(undefined);
  const [filterName, setFilterName] = useState("");
  const [filterEmail, setFilterEmail] = useState("");
  const [filterPhone, setFilterPhone] = useState("");
  const [quickDate, setQuickDate] = useState<"all" | "today" | "week" | "month">("all");

  const customers = useMemo(() => listCustomers(), [version]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "auth_user") setUser(getStoredAuthUser());
    };
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("storage", onStorage);
    window.addEventListener("auth_user_updated", onAuth);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth_user_updated", onAuth);
    };
  }, []);

  const cityOptions = useMemo(() => {
    const set = new Set<string>();
    for (const c of customers) {
      const city = c.city?.trim();
      if (city) set.add(city);
    }
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [customers]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setIsBootstrapping(true);
      setIsForbidden(false);
      try {
        await syncCustomersFromApi();
        if (mounted) refresh();
      } catch (error) {
        if (mounted && isAxiosError(error) && error.response?.status === 403) {
          setIsForbidden(true);
          return;
        }
        console.error("[Customers] Load failed", error);
        if (mounted) toast.error("Could not fetch customers. Showing cached data if any.");
      } finally {
        if (mounted) setIsBootstrapping(false);
      }
    };
    void load();
    return () => {
      mounted = false;
    };
  }, [refresh]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return customers.filter((c) => {
      if (filterCityId !== "__all__") {
        const city = (c.city ?? "").trim();
        if (city !== filterCityId) return false;
      }
      if (filterCreatedDate) {
        const want = format(filterCreatedDate, "yyyy-MM-dd");
        const created = parseISO(c.createdAt);
        const got = isValid(created) ? format(created, "yyyy-MM-dd") : null;
        if (got !== want) return false;
      }
      if (quickDate !== "all") {
        const created = parseISO(c.createdAt);
        if (!isValid(created)) return false;
        const now = new Date();
        const end = endOfDay(now);
        const start =
          quickDate === "today"
            ? startOfDay(now)
            : quickDate === "week"
              ? startOfDay(subDays(now, 6))
              : startOfMonth(now);
        if (created < start || created > end) return false;
      }
      const nameNeedle = filterName.trim().toLowerCase();
      if (nameNeedle && !c.name.toLowerCase().includes(nameNeedle)) return false;
      const emailNeedle = filterEmail.trim().toLowerCase();
      if (emailNeedle && !c.email.toLowerCase().includes(emailNeedle)) return false;
      const phoneNeedle = filterPhone.replace(/\s/g, "").toLowerCase();
      if (phoneNeedle && !c.phone.replace(/\s/g, "").toLowerCase().includes(phoneNeedle)) return false;
      if (!q) return true;
      return (
        c.name.toLowerCase().includes(q) ||
        c.fatherName.toLowerCase().includes(q) ||
        c.cnic.toLowerCase().includes(q) ||
        c.address.toLowerCase().includes(q) ||
        c.phone.toLowerCase().includes(q) ||
        c.email.toLowerCase().includes(q) ||
        c.city.toLowerCase().includes(q)
      );
    });
  }, [customers, query, filterCityId, filterCreatedDate, filterName, filterEmail, filterPhone, quickDate]);

  const sorted = useMemo(() => {
    const items = [...filtered];
    items.sort((a, b) => {
      switch (sortBy) {
        case "spent_asc":
          return a.totalSpent - b.totalSpent;
        case "spent_desc":
          return b.totalSpent - a.totalSpent;
        case "latest":
          return b.createdAt.localeCompare(a.createdAt);
        case "oldest":
          return a.createdAt.localeCompare(b.createdAt);
        case "name_desc":
          return b.name.localeCompare(a.name);
        case "name_asc":
        default:
          return a.name.localeCompare(b.name);
      }
    });
    return items;
  }, [filtered, sortBy]);

  const appliedFiltersCount =
    (filterCityId !== "__all__" ? 1 : 0) +
    (filterCreatedDate ? 1 : 0) +
    (filterName.trim() ? 1 : 0) +
    (filterEmail.trim() ? 1 : 0) +
    (filterPhone.trim() ? 1 : 0);

  const paginationKey = `${query}|${sortBy}|${quickDate}|${filterCityId}|${filterCreatedDate?.getTime() ?? ""}|${filterName}|${filterEmail}|${filterPhone}`;

  const {
    page,
    setPage,
    pageSize,
    setPageSize,
    totalCount,
    totalPages,
    paginatedItems: paginated,
    rangeStart,
    rangeEnd,
  } = useGridPagination(sorted, paginationKey, {
    pageSizeOptions: CUSTOMERS_PAGE_SIZE_OPTIONS,
    defaultPageSize: CUSTOMERS_PAGE_SIZE_OPTIONS[0],
  });

  const openCreate = () => {
    if (!canCreate) {
      toast.error("You don’t have permission to create customers.");
      return;
    }
    setEditing(null);
    setDraft({ ...defaultDraft });
    setFormOpen(true);
  };

  const openEdit = (c: CustomerRecord) => {
    if (!canUpdate) {
      toast.error("You don’t have permission to edit customers.");
      return;
    }
    setEditing(c);
    setDraft(recordToCustomerDraft(c));
    setFormOpen(true);
  };

  const saveCustomer = async () => {
    if (isSaving) return;
    if (editing && !canUpdate) {
      toast.error("You don’t have permission to edit customers.");
      return;
    }
    if (!editing && !canCreate) {
      toast.error("You don’t have permission to create customers.");
      return;
    }
    const validation = validateCustomerDraft(draft);
    if (!validation.valid) {
      toast.error(validation.message ?? "Complete all required customer fields.");
      return;
    }
    setIsSaving(true);
    try {
      const payload = normalizeCustomerDraft(draft);
      if (editing) {
        const result = await updateCustomer(editing.id, payload);
        if (!result) {
          toast.error("Customer not found.");
          return;
        }
        toast.success(result.message);
      } else {
        const result = await createCustomer(payload);
        toast.success(result.message);
      }
      setFormOpen(false);
      setEditing(null);
      try {
        await syncCustomersFromApi();
      } catch (syncError) {
        console.error("[Customers] Re-sync after save failed", syncError);
      }
      refresh();
    } catch (error) {
      console.error("[Customers] Save failed", error);
      toast.error(customerErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget || isDeleting) return;
    if (!canDelete) {
      toast.error("You don’t have permission to delete customers.");
      setDeleteTarget(null);
      return;
    }
    setIsDeleting(true);
    try {
      const result = await deleteCustomer(deleteTarget.id);
      setDeleteTarget(null);
      try {
        await syncCustomersFromApi();
      } catch (syncError) {
        console.error("[Customers] Re-sync after delete failed", syncError);
      }
      refresh();
      toast.success(result.message);
    } catch (error) {
      console.error("[Customers] Delete failed", error);
      toast.error(customerErrorMessage(error));
    } finally {
      setIsDeleting(false);
    }
  };

  const renderFormFields = () => (
    <CustomerFormFields draft={draft} onChange={setDraft} mode="full" disabled={isSaving} />
  );

  return isForbidden ? (
    <PermissionDenied
      title="Customers access required"
      message="You don’t have permission to view customers. Ask an administrator to grant customer_can_view_any."
    />
  ) : (
    <Layout title="Customers">
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-display font-bold text-foreground">Customers</h1>
            <p className="text-sm text-muted-foreground mt-1">Manage your customer database</p>
          </div>
          {canCreate ? (
            <Button variant="premium" className="gap-2" onClick={openCreate}>
              <Plus className="w-4 h-4" /> Add Customer
            </Button>
          ) : null}
        </div>

        <PartyUpsertDialog
          open={formOpen}
          onOpenChange={(o) => {
            setFormOpen(o);
            if (!o) setEditing(null);
          }}
          title={editing ? "Edit customer" : "Add customer"}
          description={
            editing
              ? "Update this customer's details and contact information."
              : "Add a customer to your directory with contact and address details."
          }
          icon={User}
          accent="sky"
          footer={
            <>
              <Button variant="outline" onClick={() => setFormOpen(false)} disabled={isSaving}>
                Cancel
              </Button>
              <Button
                onClick={() => void saveCustomer()}
                disabled={isSaving || !isCustomerDraftComplete(draft)}
                className="gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-5"
              >
                {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {editing ? "Save customer" : "Add customer"}
              </Button>
            </>
          }
        >
          {renderFormFields()}
        </PartyUpsertDialog>

        <Dialog open={!!viewCustomer} onOpenChange={(o) => !o && setViewCustomer(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Customer details</DialogTitle>
            </DialogHeader>
            {viewCustomer ? (
              <dl className="grid grid-cols-1 gap-3 text-sm">
                <div>
                  <dt className="text-xs text-muted-foreground">
                    {viewCustomer.entityKind === "business" ? "Business Name" : "Customer Name with Father Name"}
                  </dt>
                  <dd className="font-medium text-foreground">{formatCustomerDisplayName(viewCustomer)}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">Customer Type</dt>
                  <dd className="text-muted-foreground">{partyEntityKindLabel(viewCustomer.entityKind)}</dd>
                </div>
                {viewCustomer.entityKind === "business" ? (
                  <>
                    <div>
                      <dt className="text-xs text-muted-foreground">NTN / Registration No.</dt>
                      <dd className="text-muted-foreground">{viewCustomer.ntn || "—"}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-muted-foreground">Contact Person</dt>
                      <dd className="text-muted-foreground">{viewCustomer.contactPerson || "—"}</dd>
                    </div>
                  </>
                ) : (
                  <div>
                    <dt className="text-xs text-muted-foreground">CNIC #</dt>
                    <dd className="text-muted-foreground">{viewCustomer.cnic || "—"}</dd>
                  </div>
                )}
                <div>
                  <dt className="text-xs text-muted-foreground">Contact Number</dt>
                  <dd className="text-muted-foreground">{viewCustomer.phone || "—"}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">Email</dt>
                  <dd className="text-muted-foreground">{viewCustomer.email || "—"}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">Type</dt>
                  <dd className="text-muted-foreground">{partyTypeLabel(viewCustomer.type, "customer")}</dd>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <dt className="text-xs text-muted-foreground">Country</dt>
                    <dd className="text-muted-foreground">{viewCustomer.countryName || "—"}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">State</dt>
                    <dd className="text-muted-foreground">{viewCustomer.stateName || "—"}</dd>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <dt className="text-xs text-muted-foreground">City</dt>
                    <dd className="text-muted-foreground">{viewCustomer.city || "—"}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">Postal Code</dt>
                    <dd className="text-muted-foreground">{viewCustomer.postalCode || "—"}</dd>
                  </div>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">Address Line 1</dt>
                  <dd className="text-muted-foreground whitespace-pre-wrap">{viewCustomer.addressLine1 || "—"}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">Address Line 2</dt>
                  <dd className="text-muted-foreground whitespace-pre-wrap">{viewCustomer.addressLine2 || "—"}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">Purchases</dt>
                  <dd className="text-muted-foreground">{viewCustomer.totalPurchases}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">Total spent</dt>
                  <dd className="text-primary font-medium">
                    {formatCustomerSpent(viewCustomer.totalSpent, viewCustomer.currency)}
                  </dd>
                </div>
              </dl>
            ) : null}
            <DialogFooter>
              <Button variant="glass" onClick={() => setViewCustomer(null)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, CNIC, phone, address, email, city..."
                className="glass-input pl-9 h-10"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
              <SheetTrigger asChild>
                <Button variant="glass" className="gap-2 shrink-0">
                  <Filter className="w-4 h-4" />
                  Filters
                  {appliedFiltersCount > 0 ? (
                    <span className="ml-0.5 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                      {appliedFiltersCount}
                    </span>
                  ) : null}
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[92vw] sm:w-[420px] overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>Filter customers</SheetTitle>
                  <SheetDescription>Refine by name, email, phone, city, and signup date.</SheetDescription>
                </SheetHeader>
                <div className="mt-6 space-y-5">
                  <div className="rounded-xl border border-border/50 bg-muted/15 p-4 space-y-4">
                    <p className="text-sm font-medium text-foreground">Contact</p>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Name contains</Label>
                      <Input
                        className="glass-input h-10"
                        value={filterName}
                        onChange={(e) => setFilterName(e.target.value)}
                        placeholder="e.g. Ahmed"
                        autoComplete="off"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Email contains</Label>
                      <Input
                        className="glass-input h-10"
                        type="text"
                        inputMode="email"
                        value={filterEmail}
                        onChange={(e) => setFilterEmail(e.target.value)}
                        placeholder="e.g. gmail.com"
                        autoComplete="off"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Phone contains</Label>
                      <Input
                        className="glass-input h-10"
                        type="tel"
                        value={filterPhone}
                        onChange={(e) => setFilterPhone(e.target.value)}
                        placeholder="e.g. 300 or +92"
                        autoComplete="off"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">City</Label>
                    <Select value={filterCityId} onValueChange={setFilterCityId}>
                      <SelectTrigger className="glass-input h-10">
                        <SelectValue placeholder="All cities" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__all__">All cities</SelectItem>
                        {cityOptions.map((city) => (
                          <SelectItem key={city} value={city}>
                            {city}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Created date</Label>
                    <Popover modal={true}>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          className={cn(
                            "glass-input h-10 w-full justify-start text-left font-normal",
                            !filterCreatedDate && "text-muted-foreground",
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4 shrink-0 opacity-70" />
                          {filterCreatedDate ? format(filterCreatedDate, "PPP") : "Any date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="z-[100] w-auto p-0" align="start" sideOffset={6}>
                        <Calendar mode="single" selected={filterCreatedDate} onSelect={setFilterCreatedDate} initialFocus />
                        <div className="flex gap-2 border-t p-2">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="flex-1"
                            onClick={() => setFilterCreatedDate(undefined)}
                          >
                            Clear
                          </Button>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
                <SheetFooter className="mt-6 gap-2">
                  <Button
                    type="button"
                    variant="glass"
                    className="w-full"
                    onClick={() => {
                      setFilterCityId("__all__");
                      setFilterCreatedDate(undefined);
                      setFilterName("");
                      setFilterEmail("");
                      setFilterPhone("");
                    }}
                  >
                    Reset
                  </Button>
                  <SheetClose asChild>
                    <Button type="button" variant="premium" className="w-full">
                      Apply filters
                    </Button>
                  </SheetClose>
                </SheetFooter>
              </SheetContent>
            </Sheet>
          </div>

          <div className="flex items-center justify-between gap-2 md:hidden">
            <div className="overflow-x-auto flex-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background whitespace-nowrap">
                {(
                  [
                    { id: "today" as const, label: "Today", mobile: "Today" },
                    { id: "week" as const, label: "Week", mobile: "7D" },
                    { id: "month" as const, label: "Month", mobile: "30D" },
                    { id: "all" as const, label: "All", mobile: "All" },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setQuickDate(opt.id)}
                    className={cn(
                      "h-full px-3 text-sm transition-colors",
                      quickDate === opt.id
                        ? "bg-primary/10 text-foreground font-medium"
                        : "text-muted-foreground hover:bg-muted/50",
                    )}
                  >
                    <span className="sm:hidden">{opt.mobile}</span>
                    <span className="hidden sm:inline">{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0">
              <button
                type="button"
                onClick={() => setViewMode("grid")}
                className={cn(
                  "h-full px-3 text-sm transition-colors",
                  viewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                )}
                aria-label="Grid view"
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setViewMode("list")}
                className={cn(
                  "h-full px-3 text-sm transition-colors",
                  viewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                )}
                aria-label="List view"
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between gap-2 md:hidden">
            <GridPerPageSelect
              id="customers-page-size"
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              options={CUSTOMERS_PAGE_SIZE_OPTIONS}
            />
            <div className="w-[170px] shrink-0">
              <Select value={sortBy} onValueChange={(v) => setSortBy(v as CustomerSortBy)}>
                <SelectTrigger className="glass-input h-10 w-full">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="spent_desc">Spent: High to Low</SelectItem>
                  <SelectItem value="spent_asc">Spent: Low to High</SelectItem>
                  <SelectItem value="latest">Newest first</SelectItem>
                  <SelectItem value="oldest">Oldest first</SelectItem>
                  <SelectItem value="name_asc">Name: A to Z</SelectItem>
                  <SelectItem value="name_desc">Name: Z to A</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="hidden md:flex items-center justify-between gap-3">
            <GridPerPageSelect
              id="customers-page-size-desktop"
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              options={CUSTOMERS_PAGE_SIZE_OPTIONS}
            />
            <div className="flex items-center gap-3 justify-end min-w-0">
              <div className="w-[190px] shrink-0">
                <Select value={sortBy} onValueChange={(v) => setSortBy(v as CustomerSortBy)}>
                  <SelectTrigger className="glass-input h-10 w-full">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="spent_desc">Spent: High to Low</SelectItem>
                    <SelectItem value="spent_asc">Spent: Low to High</SelectItem>
                    <SelectItem value="latest">Newest first</SelectItem>
                    <SelectItem value="oldest">Oldest first</SelectItem>
                    <SelectItem value="name_asc">Name: A to Z</SelectItem>
                    <SelectItem value="name_desc">Name: Z to A</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background whitespace-nowrap">
                {(
                  [
                    { id: "today" as const, label: "Today" },
                    { id: "week" as const, label: "Week" },
                    { id: "month" as const, label: "Month" },
                    { id: "all" as const, label: "All" },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setQuickDate(opt.id)}
                    className={cn(
                      "h-full px-3 text-sm transition-colors",
                      quickDate === opt.id
                        ? "bg-primary/10 text-foreground font-medium"
                        : "text-muted-foreground hover:bg-muted/50",
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0">
                <button
                  type="button"
                  onClick={() => setViewMode("grid")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="Grid view"
                >
                  <LayoutGrid className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode("list")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="List view"
                >
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {isBootstrapping ? (
          <div className="glass-card p-6 text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading customers…
          </div>
        ) : null}

        {!isBootstrapping && filtered.length === 0 ? (
          <div className="glass-card p-6 text-sm text-muted-foreground">
            {customers.length === 0
              ? canCreate
                ? "No customers yet. Click “Add Customer” to create one."
                : "No customers yet."
              : "No customers match your search."}
          </div>
        ) : null}

        {!isBootstrapping && filtered.length > 0 && viewMode === "grid" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {paginated.map((c, i) => (
              <motion.div
                key={c.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
              >
                <CatalogEntityCard
                  title={formatCustomerDisplayName(c)}
                  titleIcon={User}
                  primaryMeta={{
                    label: "City",
                    value: c.city || "—",
                    icon: MapPin,
                    iconClassName: "text-emerald-600",
                  }}
                  secondaryMeta={{
                    label: "Purchases",
                    value: String(c.totalPurchases),
                    icon: ShoppingBag,
                    iconClassName: "text-red-600",
                  }}
                  footer={`Last updated: ${formatCatalogAuditDate(c.updatedAt)}`}
                  footerRight={formatCustomerSpent(c.totalSpent, c.currency)}
                  onView={canView ? () => setViewCustomer(c) : undefined}
                  onEdit={canUpdate ? () => openEdit(c) : undefined}
                  onDelete={canDelete ? () => setDeleteTarget(c) : undefined}
                />
              </motion.div>
            ))}
          </div>
        ) : null}

        {!isBootstrapping && filtered.length > 0 && viewMode === "list" ? (
          <div className="space-y-3">
            {paginated.map((c, i) => (
              <motion.div
                key={c.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.02 }}
                className="glass-card p-3"
              >
                <div className="flex flex-wrap items-center gap-3">
                  <div className="h-16 w-20 rounded-md overflow-hidden bg-muted border border-border/50 shrink-0 flex items-center justify-center">
                    <Users className="w-8 h-8 text-muted-foreground/40" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-sm font-semibold text-foreground truncate">{formatCustomerDisplayName(c)}</h3>
                      <span className="text-[11px] px-2 py-0.5 rounded-full font-medium bg-emerald-500/20 text-emerald-600">
                        {c.city?.trim() || "—"}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 truncate">
                      {[c.phone, c.cnic].filter(Boolean).join(" · ") || "—"}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {c.totalPurchases} purchase{c.totalPurchases === 1 ? "" : "s"}
                    </p>
                  </div>
                  <div className="text-right ml-auto">
                    <p className="text-sm font-semibold text-primary">{formatCustomerSpent(c.totalSpent, c.currency)}</p>
                    <p className="text-[11px] text-muted-foreground">{formatCreatedAt(c.createdAt)}</p>
                  </div>
                  <div className="flex items-center gap-2 ml-auto">
                    {canView ? (
                      <Button variant="glass" size="sm" className="gap-1.5 h-8 text-xs" onClick={() => setViewCustomer(c)}>
                        <Eye className="w-3 h-3" /> View
                      </Button>
                    ) : null}
                    {canUpdate ? (
                      <Button variant="glass" size="sm" className="gap-1.5 h-8 text-xs" onClick={() => openEdit(c)}>
                        <Edit className="w-3 h-3" /> Edit
                      </Button>
                    ) : null}
                    {canDelete ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:bg-destructive/10 h-8 w-8 p-0"
                        onClick={() => setDeleteTarget(c)}
                        aria-label="Delete customer"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    ) : null}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : null}

        {!isBootstrapping && filtered.length > 0 ? (
          <GridPaginationFooter
            totalCount={totalCount}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        ) : null}

        <ConfirmAlertDialog
          open={!!deleteTarget}
          onOpenChange={(o) => !o && setDeleteTarget(null)}
          title="Delete this customer?"
          description={
            deleteTarget ? (
              <>
                Are you sure you want to delete{" "}
                <strong className="text-foreground font-semibold">{deleteTarget.name}</strong>? This cannot be undone.
              </>
            ) : (
              ""
            )
          }
          mode="confirm"
          variant="danger"
          confirmLabel="Delete"
          cancelLabel="Cancel"
          onConfirm={() => void confirmDelete()}
        />
      </div>
    </Layout>
  );
};

export default Customers;
